import React, { useRef, useEffect } from 'react';
import './HomePage.css';
import { Github, Linkedin, Envelope } from 'react-bootstrap-icons';
import BlogWidget from '../components/blog/BlogWidget';
import ExperienceWidget from '../components/experience/ExperienceWidget';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { useLocale } from '../context/LocaleContext';
import { usePageMeta } from '../hooks/usePageMeta';

const HomePage: React.FC = () => {
  usePageMeta('brianna.dk', 'Portfolio and self-made content on immigration and integration into Denmark.');
  const { t } = useLocale();
  const mainContentRef = useRef<HTMLElement>(null);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      // Update the browser URL with hash - let browser handle navigation naturally
      window.location.hash = sectionId;
      // Scroll will be handled by hashchange event
    }
  };

  // Handle initial hash on page load and hash changes
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1); // Remove the # symbol
      if (hash) {
        const element = document.getElementById(hash);
        if (element) {
          // Small delay to ensure DOM is ready
          setTimeout(() => {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 0);
        }
      } else {
        // If no hash, scroll to top smoothly
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    };

    // Handle initial hash after a small delay to ensure DOM is ready
    const timeoutId = setTimeout(() => {
      handleHashChange();
    }, 100);

    // Listen for hash changes (triggered by browser navigation)
    window.addEventListener('hashchange', handleHashChange);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  const skipToContent = () => {
    if (mainContentRef.current) {
      mainContentRef.current.focus();
      mainContentRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="portfolio-container">
      <a href="#main-content" className="skip-to-content" onClick={(e) => { e.preventDefault(); skipToContent(); }}>
        {t('common.skipToContent')}
      </a>

      <aside className="portfolio-sidebar">
        <div className="sidebar-content">
          <h1 className="sidebar-name">Brianna Becker</h1>
          <p className="sidebar-role">Lead Software Engineer</p>
          <img src="/profile.png" alt="Brianna Becker" className="sidebar-profile" />
          <LanguageSwitcher />
          <div className="sidebar-social">
            <a href="mailto:b@brianna.dk" aria-label="Email">
              <Envelope size={20} />
            </a>
            <a href="https://github.com/bbckr" target="_blank" rel="noopener noreferrer" aria-label="GitHub">
              <Github size={20} />
            </a>
            <a href="https://www.linkedin.com/in/bckr/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
              <Linkedin size={20} />
            </a>
          </div>

          <nav className="sidebar-nav">
            <button onClick={() => scrollToSection('about')} className="nav-link">
              <span className="nav-line"></span> {t('nav.about')}
            </button>
            <button onClick={() => scrollToSection('experience')} className="nav-link">
              <span className="nav-line"></span> {t('nav.experience')}
            </button>
            <button onClick={() => scrollToSection('blog')} className="nav-link">
              <span className="nav-line"></span> {t('nav.blog')}
            </button>
            <button onClick={() => scrollToSection('projects')} className="nav-link">
              <span className="nav-line"></span> {t('nav.projects')}
            </button>
          </nav>
        </div>
      </aside>

      <main id="main-content" ref={mainContentRef} className="portfolio-content" tabIndex={-1}>
        <section id="about" className="portfolio-section">
          <h2 className="portfolio-section-title">{t('home.about.title')}</h2>
          <div className="section-content">
            <p>{t('home.about.intro')}</p>
            <p>{t('home.about.body')}</p>
            <p>{t('home.about.outro')}</p>
          </div>
        </section>

        <section id="experience" className="portfolio-section">
          <ExperienceWidget />
        </section>

        <section id="blog" className="portfolio-section">
          <BlogWidget />
        </section>

        <section id="projects" className="portfolio-section">
          <h2 className="portfolio-section-title">{t('home.projects.title')}</h2>
          <div className="section-content">
            <div className="projects-list">
              <a href="/projects/vejen-til-dansk-statsborgerskab" className="project-item">
                <div className="project-image-wrapper">
                  <img src="/projects/vejen-til-danmark.png" alt={t('home.projects.vejen.name')} className="project-image" />
                </div>
                <div className="project-content">
                  <h3 className="project-title">
                    <span className="project-link">{t('home.projects.vejen.name')}</span>
                  </h3>
                  <p className="project-description">
                    {t('home.projects.vejen.description')}
                  </p>
                  <ul className="project-tech">
                    <li>React</li>
                    <li>TypeScript</li>
                    <li>Node.js</li>
                    <li>LLMs</li>
                  </ul>
                </div>
              </a>
              <a href="/projects/fatties-nft-platform" className="project-item">
                <div className="project-image-wrapper">
                  <img src="/projects/fatties.png" alt={t('home.projects.fatties.name')} className="project-image" />
                </div>
                <div className="project-content">
                  <h3 className="project-title">
                    <span className="project-link">{t('home.projects.fatties.name')}</span>
                  </h3>
                  <p className="project-description">
                    {t('home.projects.fatties.description')}
                  </p>
                  <ul className="project-tech">
                    <li>Solidity</li>
                    <li>Hardhat</li>
                    <li>React</li>
                    <li>TypeScript</li>
                    <li>Express</li>
                    <li>IPFS</li>
                    <li>PostgreSQL</li>
                    <li>Terraform</li>
                    <li>Docker</li>
                    <li>Web3</li>
                    <li>ERC721A</li>
                  </ul>
                </div>
              </a>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default HomePage;
